import { AlertTriangle } from "lucide-react";
import PropTypes from "prop-types";
import Datatable from "@components/datatable";
import StatusChip from "@components/ui-components/status-chip";
import PaginationBar from "@components/datatable/pagination-bar";
import KycReminderDialog from "@panel/ops/publishers/components/kyc-reminder-dialog";
import KycRowActions from "@panel/ops/publishers/components/kyc-row-actions";
import KycStatusCell from "@panel/ops/publishers/components/kyc-status-cell";
import PublisherCell from "@panel/ops/publishers/components/publisher-cell";
import { KYC_EXPIRY_WARNING_DAYS } from "@panel/ops/publishers/misc/kyc";
import useBrandPublishers from "./use-publishers";
import { formatDateTime } from "@src/utility/helpers/date";
import { formatCurrency } from "@src/utility/helpers/utils";
import { PAYOUT_LABEL, PAYOUT_TONE } from "@src/utility/constants/panel/common";

const ROWS_PER_PAGE_OPTIONS = [10, 20, 50];

const Publishers = ({ brandId, brandName, isSelfPayout }) => {
  const {
    publishers,
    pagination,
    loading,
    handlePageChange,
    handlePerRowsChange,
    payPublisher,
    openPublisher,
    origin,
    kyc,
    expiringSoon,
    incomplete
  } = useBrandPublishers(brandId, brandName);

  const columns = [
    {
      Header  : "Publisher",
      accessor: "holder_name",
      Cell    : ({ row }) => <PublisherCell publisher={row} from={origin} />
    },
    {
      Header  : "KYC",
      accessor: "kyc_status",
      Cell    : ({ row }) => <KycStatusCell publisher={row} />
    },
    {
      Header  : "Earnings pending",
      accessor: "earnings_pending",
      Cell    : ({ value }) => (
        <span className="text-gray-900">{formatCurrency(value)}</span>
      )
    },
    {
      Header  : "Payout",
      accessor: "payout_status",
      Cell    : ({ value }) => (
        <StatusChip
          tone={PAYOUT_TONE[value] || "neutral"}
          label={PAYOUT_LABEL[value] || value}
        />
      )
    },
    {
      Header  : "Last payout",
      accessor: "last_payout_at",
      Cell    : ({ value }) => (
        <span className="text-gray-700">{formatDateTime(value) || "—"}</span>
      )
    },
    {
      Header  : "",
      accessor: "_id",
      Cell    : ({ row }) => (
        <KycRowActions publisher={row} kyc={kyc} from={origin}>
          {row.payout_status === "eligible" && !isSelfPayout && (
            <button
              type="button"
              onClick={() => payPublisher(row)}
              className="rounded-md bg-navy px-3 py-1.5 text-xs font-semibold text-white hover:bg-navy/90"
            >
              Pay {formatCurrency(row.earnings_pending)}
            </button>
          )}
        </KycRowActions>
      )
    }
  ];

  return (
    <div>
      {expiringSoon.length > 0 && (
        <div className="mb-4 flex items-center gap-2 rounded-lg border border-danger/20 bg-danger/5 px-4 py-3 text-sm text-danger">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          {expiringSoon.length} publisher{expiringSoon.length > 1 ? "s" : ""} —
          KYC window expires within {KYC_EXPIRY_WARNING_DAYS} days.
        </div>
      )}

      <div className="mb-4 flex justify-end">
        <button
          type="button"
          disabled={incomplete.length === 0}
          onClick={() => kyc.openBulkReminder(incomplete)}
          className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
        >
          Preview & send reminder to all incomplete ({incomplete.length})
        </button>
      </div>

      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
        <Datatable
          columns={columns}
          data={publishers}
          loading={loading}
          showPagination={false}
          onRowClicked={openPublisher}
          customNoData={
            <span className="text-sm text-muted">
              No publishers linked yet.
            </span>
          }
        />
      </div>

      <div className="mt-2">
        <PaginationBar
          pageIndex={pagination.page - 1}
          pageSize={pagination.limit}
          total={pagination.total}
          rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
          onPageChange={(pageIndex) => handlePageChange(pagination.limit, pageIndex + 1)
          }
          onRowsPerPageChange={handlePerRowsChange}
        />
      </div>

      <KycReminderDialog kyc={kyc} />
    </div>
  );
};

Publishers.propTypes = {
  brandId     : PropTypes.string.isRequired,
  brandName   : PropTypes.string.isRequired,
  isSelfPayout: PropTypes.bool
};

export default Publishers;
